import {
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router-dom';
import { ChildLocation, LocationType } from 'features/locations/location.types';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { getLocationStatusForViewer } from 'features/locations/utils/operatingHours';
import { LOCATION_HOURS_STATUS_TO_TRANS_KEY } from 'features/locations/location.constants';
import { TFunction } from 'i18next';
import { LocationTypes } from '../../../../constants';

type LocationsProps = {
  locations: ChildLocation[];
};

type LocationSection = {
  label: string;
  locations: ChildLocation[];
};

type LocationListProps = {
  header: string;
  locations: ChildLocation[];
};

const legacyLocationTypeToTransKey = {
  [LocationTypes.evacShelter]: 'geoEvent.locationTypes.shelter',
  [LocationTypes.animalShelter]: 'geoEvent.locationTypes.animalShelter',
  [LocationTypes.info]: 'geoEvent.locationTypes.info',
  [LocationTypes.general]: 'geoEvent.locationTypes.general',
  [LocationTypes.food]: 'geoEvent.locationTypes.food',
};

const locationTypeToTransKey = {
  [LocationTypes.shelter]: 'geoEvent.locationTypes.shelter',
  [LocationTypes.evacShelter]: 'geoEvent.locationTypes.evacShelter',
  [LocationTypes.animalShelter]: 'geoEvent.locationTypes.smallAnimalShelter', // for backward compatibility
  [LocationTypes.smallAnimalShelter]:
    'geoEvent.locationTypes.smallAnimalShelter',
  [LocationTypes.info]: 'geoEvent.locationTypes.info',
  [LocationTypes.general]: 'geoEvent.locationTypes.general',
  [LocationTypes.food]: 'geoEvent.locationTypes.meals', // for backward compatibility
  [LocationTypes.meals]: 'geoEvent.locationTypes.meals',
  [LocationTypes.firstAid]: 'geoEvent.locationTypes.firstAid',
  [LocationTypes.temporaryEvacPoint]:
    'geoEvent.locationTypes.temporaryEvacPoint',
  [LocationTypes.sandBags]: 'geoEvent.locationTypes.sandBags',
  [LocationTypes.water]: 'geoEvent.locationTypes.water',
  [LocationTypes.farmersMarket]: 'geoEvent.locationTypes.farmersMarket',
  [LocationTypes.foodPantry]: 'geoEvent.locationTypes.foodPantry',
  [LocationTypes.largeAnimalShelter]:
    'geoEvent.locationTypes.largeAnimalShelter',
  [LocationTypes.maskDistribution]: 'geoEvent.locationTypes.maskDistribution',
  [LocationTypes.communityDistribution]:
    'geoEvent.locationTypes.communityDistribution',
};

const typeOrderIndex: Record<LocationType, number> = {
  [LocationTypes.shelter]: 2,
  [LocationTypes.evacShelter]: 1,
  [LocationTypes.animalShelter]: 11, // for backward compatibility
  [LocationTypes.smallAnimalShelter]: 11,
  [LocationTypes.info]: 13,
  [LocationTypes.general]: 14,
  [LocationTypes.food]: 8, // for backward compatibility
  [LocationTypes.meals]: 8,
  [LocationTypes.firstAid]: 0,
  [LocationTypes.temporaryEvacPoint]: 3,
  [LocationTypes.sandBags]: 6,
  [LocationTypes.water]: 7,
  [LocationTypes.farmersMarket]: 9,
  [LocationTypes.foodPantry]: 10,
  [LocationTypes.largeAnimalShelter]: 12,
  [LocationTypes.maskDistribution]: 4,
  [LocationTypes.communityDistribution]: 5,
};

// Sort unknown location types to the bottom to support older clients
const orderIndexOf = (locationType: LocationType): number =>
  typeOrderIndex[locationType] ?? Number.MAX_SAFE_INTEGER;

const useStyles = makeStyles()((theme) => ({
  list: {
    paddingTop: 0,
  },
  listItem: {
    marginRight: theme.spacing(1),
  },
  textOneLine: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const getLocationStatusLabel = (
  t: TFunction,
  location: ChildLocation,
): string => {
  if (location.data.atCapacity) {
    return t('location.atCapacity');
  }
  if (location.data.hours.length) {
    const hoursStatus = getLocationStatusForViewer(location);
    if (hoursStatus === 'open') return '';
    return t(LOCATION_HOURS_STATUS_TO_TRANS_KEY[hoursStatus]);
  }
  return '';
};

const LocationList = (props: LocationListProps): JSX.Element => {
  const { header, locations } = props;
  const { classes } = useStyles();
  const history = useHistory();
  const showLocationV2 = useLocationV2Enabled();
  const { t } = useTranslation();

  const sortedLocations = locations.sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );

  return (
    <List
      subheader={
        <ListSubheader variant="watchduty-section-header">
          <Typography variant="h3">{header}</Typography>
        </ListSubheader>
      }
    >
      {sortedLocations.map((location) => {
        const locationStatusLabel = getLocationStatusLabel(t, location);
        return (
          <ListItemButton
            key={location.id}
            divider
            onClick={() =>
              history.push(`/location/${location.id}`, {
                fromRoute: window.location.pathname + window.location.search,
              })
            }
          >
            <ListItemText
              primary={
                <Stack
                  component="span"
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography
                    component="span"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 'fontWeightBold',
                    }}
                  >
                    {location.name}
                  </Typography>
                  {showLocationV2 && Boolean(locationStatusLabel) && (
                    <Chip
                      label={locationStatusLabel}
                      variant="watchduty"
                      color="error"
                      component="span"
                      data-testid="location-status"
                      sx={{ minWidth: 'fit-content' }}
                    />
                  )}
                </Stack>
              }
              secondary={location.address}
              className={classes.listItem}
              slotProps={{
                primary: {
                  className: classes.textOneLine,
                },
                secondary: {
                  variant: 'body1',
                  color: 'textPrimary',
                  className: classes.textOneLine,
                },
              }}
            />
            <ChevronRightIcon />
          </ListItemButton>
        );
      })}
    </List>
  );
};

export const Locations = (props: LocationsProps): JSX.Element => {
  const { locations } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const showLocationV2 = useLocationV2Enabled();

  const typeToTransKey = showLocationV2
    ? locationTypeToTransKey
    : legacyLocationTypeToTransKey;

  const locationSections = locations.reduce(
    (sections, location) => {
      const sectionLabel = t(
        // @ts-expect-error - type mismatch is due to backward compatibility
        typeToTransKey[location.data.locationType] ??
          'geoEvent.locationTypes.general',
      );
      if (!sections[sectionLabel]) {
        // eslint-disable-next-line no-param-reassign
        sections[sectionLabel] = {
          label: sectionLabel,
          locations: [location],
        };
      } else {
        sections[sectionLabel].locations.push(location);
      }
      return sections;
    },
    {} as Record<string, LocationSection>,
  );

  const sortedLocationsSections = showLocationV2
    ? Object.values(locationSections).sort((a, b) => {
        const firstLocA = a.locations[0];
        const firstLocB = b.locations[0];
        return (
          orderIndexOf(firstLocA.data.locationType) -
          orderIndexOf(firstLocB.data.locationType)
        );
      })
    : Object.values(locationSections);

  return (
    <List className={classes.list}>
      {sortedLocationsSections.map((section) => (
        <LocationList
          key={section.label}
          header={section.label}
          locations={section.locations}
        />
      ))}
    </List>
  );
};
